import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { ThreeDots } from "react-loader-spinner";
import { useSnackbar } from "notistack";
import theme from "../../theme";
import CommonButton from "../button";
import { toMessages } from "./utils";
import {
  getFortressCompaniesAction,
  getFortressCompaniesErrorAction,
  updateFortressCompaniesAction,
  updateFortressCompaniesSuccessAction,
  updateFortressCompaniesErrorAction,
  getCompanyListAction,
} from "../../containers/dashboard/action";

const SEARCH_PAGE_SIZE = 8;

const useStyles = makeStyles(() => ({
  wrap: { maxWidth: "760px" },
  legend: {
    display: "flex",
    gap: "18px",
    flexWrap: "wrap",
    marginBottom: "18px",
  },
  legendItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    fontSize: "13px",
    fontFamily: theme.fontFamily.MARKPROBOOK,
    color: theme.colors.Violet,
  },
  dot: { width: "10px", height: "10px", borderRadius: "50%" },
  dotFortress: { backgroundColor: theme.colors.DodgerBlue },
  dotPaystack: { backgroundColor: theme.colors.MintGreen },
  addBox: {
    border: `1px solid ${theme.colors.Zumthor}`,
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "20px",
  },
  addLabel: {
    fontSize: "14px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    color: theme.colors.Violet,
    marginBottom: "8px",
  },
  searchWrap: { position: "relative" },
  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 12px",
    fontSize: "14px",
    fontFamily: theme.fontFamily.MARKPRO,
    color: theme.colors.Violet,
    backgroundColor: theme.colors.Zumthor,
    border: `1px solid ${theme.colors.Zumthor}`,
    borderRadius: "6px",
    outline: "none",
  },
  results: {
    marginTop: "8px",
    border: `1px solid ${theme.colors.Zumthor}`,
    borderRadius: "8px",
    maxHeight: "230px",
    overflowY: "auto",
  },
  resultRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "9px 12px",
    borderBottom: `1px solid ${theme.colors.Zumthor}`,
    cursor: "pointer",
    gap: "10px",
    "&:hover": { backgroundColor: theme.colors.Zumthor },
  },
  resultName: {
    fontSize: "14px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    color: theme.colors.Violet,
  },
  addTag: {
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.DodgerBlue,
    whiteSpace: "nowrap",
  },
  searchState: {
    padding: "14px",
    textAlign: "center",
    fontSize: "13px",
    fontFamily: theme.fontFamily.MARKPROBOOK,
    color: theme.colors.Violet,
    opacity: 0.7,
  },
  listTitle: {
    fontSize: "15px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.Violet,
    marginBottom: "10px",
  },
  chips: { display: "flex", flexWrap: "wrap", gap: "8px", minHeight: "34px" },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: theme.colors.Zumthor,
    color: theme.colors.Violet,
    borderRadius: "999px",
    padding: "5px 12px",
    fontSize: "13px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    maxWidth: "100%",
  },
  chipX: {
    cursor: "pointer",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.DodgerBlue,
  },
  emptyChips: {
    fontSize: "13px",
    fontFamily: theme.fontFamily.MARKPROBOOK,
    color: theme.colors.Violet,
    opacity: 0.6,
  },
  footer: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginTop: "26px",
  },
  saveBtn: { width: "180px" },
  dirtyNote: {
    fontSize: "13px",
    fontFamily: theme.fontFamily.MARKPROBOOK,
    color: theme.colors.DodgerBlue,
  },
  loadingRow: { display: "flex", justifyContent: "center", padding: "30px" },
}));

const sortedIds = (arr) => arr.map((c) => c.id).sort().join("|");

function FortressCompanies() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  // Single form-object state (+ spread updates) instead of one useState per field.
  const [form, setForm] = useState({ searchTerm: "", selected: [] }); // selected: [{ id, name }]
  const { searchTerm, selected } = form;
  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const fortressCompanies = useSelector(
    (s) => s.dashboardReducer?.fortressCompanies || []
  );
  const listLoading = useSelector(
    (s) => !!s.dashboardReducer?.fortressCompaniesLoading
  );
  const listError = useSelector((s) => s.dashboardReducer?.fortressCompaniesError);
  const saving = useSelector(
    (s) => !!s.dashboardReducer?.updateFortressCompaniesLoading
  );
  const saveSuccess = useSelector(
    (s) => s.dashboardReducer?.updateFortressCompaniesSuccess
  );
  const saveError = useSelector(
    (s) => s.dashboardReducer?.updateFortressCompaniesError
  );
  const companyList = useSelector((s) => s.dashboardReducer?.companyList);
  const searching = useSelector((s) => !!s.dashboardReducer?.loading);

  // Load the current Fortress set on mount.
  useEffect(() => {
    dispatch(getFortressCompaniesAction());
  }, [dispatch]);

  // Sync the working selection whenever the saved set changes (initial load + save).
  useEffect(() => {
    setField(
      "selected",
      (fortressCompanies || []).map((c) => ({ id: c.id, name: c.name }))
    );
  }, [fortressCompanies]);

  // Debounced company search.
  useEffect(() => {
    const term = searchTerm.trim();
    if (!term) return undefined;
    const handle = setTimeout(() => {
      dispatch(
        getCompanyListAction({ search: term, page: 1, page_size: SEARCH_PAGE_SIZE })
      );
    }, 400);
    return () => clearTimeout(handle);
  }, [searchTerm, dispatch]);

  useEffect(() => {
    if (!listError) return;
    toMessages(listError, "Unable to load Fortress companies.").forEach((m) =>
      enqueueSnackbar(m, { variant: "error" })
    );
    dispatch(getFortressCompaniesErrorAction(null));
  }, [listError, enqueueSnackbar, dispatch]);

  useEffect(() => {
    if (!saveSuccess) return;
    enqueueSnackbar("Gateway assignment saved.", { variant: "success" });
    dispatch(updateFortressCompaniesSuccessAction(null));
    setField("searchTerm", "");
  }, [saveSuccess, enqueueSnackbar, dispatch]);

  useEffect(() => {
    if (!saveError) return;
    toMessages(saveError, "Unable to save gateway assignment.").forEach((m) =>
      enqueueSnackbar(m, { variant: "error" })
    );
    dispatch(updateFortressCompaniesErrorAction(null));
  }, [saveError, enqueueSnackbar, dispatch]);

  const selectedIds = useMemo(() => new Set(selected.map((c) => c.id)), [selected]);

  const searchResults = useMemo(() => {
    const data = Array.isArray(companyList?.data) ? companyList.data : [];
    return data.filter((c) => c?.id && !selectedIds.has(c.id));
  }, [companyList, selectedIds]);

  const addCompany = (c) => {
    if (selectedIds.has(c.id)) return;
    setForm((prev) => ({
      ...prev,
      selected: [...prev.selected, { id: c.id, name: c.name }],
    }));
  };
  const removeCompany = (id) =>
    setForm((prev) => ({
      ...prev,
      selected: prev.selected.filter((c) => c.id !== id),
    }));

  const dirty = sortedIds(selected) !== sortedIds(fortressCompanies || []);

  const handleSave = () => {
    dispatch(updateFortressCompaniesAction({ companies: selected.map((c) => c.id) }));
  };

  const handleReset = () => {
    setForm({
      searchTerm: "",
      selected: (fortressCompanies || []).map((c) => ({ id: c.id, name: c.name })),
    });
  };

  const trimmed = searchTerm.trim();

  if (listLoading && !fortressCompanies.length) {
    return (
      <div className={classes.loadingRow}>
        <ThreeDots color={theme.colors.DodgerBlue} height={46} width={46} />
      </div>
    );
  }

  return (
    <div className={classes.wrap}>
      <div className={classes.legend}>
        <span className={classes.legendItem}>
          <span className={`${classes.dot} ${classes.dotFortress}`} /> On Fortress
          ({selected.length})
        </span>
        <span className={classes.legendItem}>
          <span className={`${classes.dot} ${classes.dotPaystack}`} /> Everyone else
          settles via Paystack
        </span>
      </div>

      <div className={classes.addBox}>
        <div className={classes.addLabel}>Add a company to Fortress</div>
        <div className={classes.searchWrap}>
          <input
            className={classes.searchInput}
            placeholder="Search companies by name"
            value={searchTerm}
            onChange={(e) => setField("searchTerm", e.target.value)}
            aria-label="Search companies"
          />
          {trimmed ? (
            <div className={classes.results}>
              {searching ? (
                <div className={classes.searchState}>Searching…</div>
              ) : searchResults.length === 0 ? (
                <div className={classes.searchState}>
                  No companies found for “{trimmed}”.
                </div>
              ) : (
                searchResults.map((c) => (
                  <div
                    key={c.id}
                    className={classes.resultRow}
                    onClick={() => addCompany(c)}
                  >
                    <span className={classes.resultName}>{c.name || c.id}</span>
                    <span className={classes.addTag}>+ Add to Fortress</span>
                  </div>
                ))
              )}
            </div>
          ) : null}
        </div>
      </div>

      <div className={classes.listTitle}>
        Companies on Fortress ({selected.length})
      </div>
      <div className={classes.chips}>
        {selected.length === 0 ? (
          <span className={classes.emptyChips}>
            No companies on Fortress — all companies settle via Paystack.
          </span>
        ) : (
          selected.map((c) => (
            <span key={c.id} className={classes.chip} title={c.name}>
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "220px",
                  whiteSpace: "nowrap",
                }}
              >
                {c.name || c.id}
              </span>
              <span
                className={classes.chipX}
                onClick={() => removeCompany(c.id)}
                aria-label={`Remove ${c.name}`}
              >
                ×
              </span>
            </span>
          ))
        )}
      </div>

      <div className={classes.footer}>
        <div className={classes.saveBtn}>
          <CommonButton
            type="button"
            text="Save changes"
            stylevariant="primary"
            fontSize="15px"
            loading={saving}
            disabled={saving || !dirty}
            onClick={handleSave}
          />
        </div>
        {dirty ? (
          <>
            <span className={classes.dirtyNote}>Unsaved changes</span>
            <span
              className={classes.chipX}
              style={{ cursor: "pointer" }}
              onClick={saving ? undefined : handleReset}
            >
              Reset
            </span>
          </>
        ) : null}
      </div>
    </div>
  );
}

export default FortressCompanies;
